import type { ParsedUrlQuery } from 'querystring';
import { stringify } from 'querystring';

import { ApiError } from './errors';
import type { ApiRequest, ApiResponse } from './types';
import { MimeBinary, MimeForm, MimeFormData, MimeImage, MimeJson, MimePdf } from './types';

const createUrl = (request: ApiRequest) => {
  const { host = '' } = request;
  const path = getPath(request);
  const query = getQuery(request);

  return `${host}${path}${query}`;
};

const createOptions = (request: ApiRequest) => {
  const { mode = 'cors', method = 'GET', credentials } = request;
  const headers = getHeaders(request);

  const body = getBody(request);

  const options: RequestInit = {
    mode,
    method,
    headers,
    credentials,
  };

  if (body != null) {
    options.body = body;
  }

  return options;
};

const getPath = (request: ApiRequest) => {
  const { path } = request;

  return path;
};

const getQuery = (request: ApiRequest) => {
  const { query } = request;

  return query != null ? `?${stringify(query as ParsedUrlQuery)}` : '';
};

const getBody = (request: ApiRequest) => {
  const { body, contentType } = request;

  if (body == null) {
    return null;
  }

  if (contentType === MimeFormData) {
    return body as FormData;
  }

  if (typeof body === 'object') {
    if (contentType === MimeForm) {
      return stringify(body as ParsedUrlQuery);
    }

    return JSON.stringify(body);
  }

  return body;
};

const getHeaders = (request: ApiRequest) => {
  const { body, contentType, responseType = MimeJson, headers } = request;

  const finalHeaders = new Headers({
    ...headers,
    accept: responseType,
  });

  // istanbul ignore else
  if (body != null && contentType !== MimeFormData) {
    finalHeaders.append('content-type', contentType || MimeJson);
  }

  return finalHeaders;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GetResponseBody = (response: Response) => Promise<any>;
export const getResponseBody: GetResponseBody = (response) => {
  const contentType = response.headers.get('content-type');
  const isBinaryContentType = contentType && contentType.includes(MimeBinary);
  const isImageBlobContentType = contentType && contentType.startsWith(MimeImage);
  const isPdfBlobContentType = contentType && contentType.startsWith(MimePdf);
  if (isBinaryContentType || isImageBlobContentType || isPdfBlobContentType) {
    return response.blob();
  }

  if (contentType && contentType.includes(MimeJson)) {
    return response.json();
  }

  return response.text();
};

const handleResponse = (request: ApiRequest, response: Response) => {
  if (response.ok || response.type === 'opaque' || response.type === 'opaqueredirect') {
    return getResponseBody(response);
  }

  throw new ApiError(request, response.statusText, response.status, response.headers);
};

type ExecuteRequest = (request: ApiRequest) => Promise<Response>;
export const executeRequest: ExecuteRequest = (request) => {
  const url = createUrl(request);
  const options = createOptions(request);

  return fetch(url, options);
};

type InvokeApiRequest = <TResponse>(request: ApiRequest) => Promise<ApiResponse<TResponse>>;
export const apiRequest: InvokeApiRequest = async (request) => {
  try {
    const response = await executeRequest(request);
    const body = await handleResponse(request, response);

    return { body, headers: response.headers };
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(request, error);
  }
};
